import { inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { ConfirmationService } from './confirmation.service';
import { ToastService } from './toast.service';
import { ErrorResponse, translateWellKnownError, WKECodes } from '../utils/errors';

const genericToasts = {
  genericSuccessSummary: 'i18n.GenericToast.successSummary',
  genericSuccessDetail: 'i18n.GenericToast.successDetail',
  genericFailSummary: 'i18n.GenericToast.failSummary',
  genericFailDetail: 'i18n.GenericToast.failDetail',
};

export abstract class BaseService {
  abstract readonly toasts: {
    createSuccessSummary: string;
    createSuccessDetail: string;
    createFailSummary: string;
    createFailDetail: string;

    updateSuccessSummary: string;
    updateSuccessDetail: string;
    updateFailSummary: string;
    updateFailDetail: string;

    deleteFailSummary: string;
    deleteFailDetail: string;
    deleteSuccessSummary: string;
    deleteSuccessDetail: string;
  };

  readonly confirmDelete = {
    header: 'i18n.ConfirmDelete.header',
    message: 'i18n.ConfirmDelete.message',
    confirmLabel: 'i18n.Global.delete',
  };

  protected readonly translateService = inject(TranslateService);

  showCreateSuccessToast(toast: ToastService) {
    toast.add({
      severity: 'info',
      summary: this.toasts.createSuccessSummary,
      detail: this.toasts.createSuccessDetail,
    });
  }

  showCreateFailToast(toast: ToastService) {
    toast.add({
      severity: 'error',
      summary: this.toasts.createFailSummary,
      detail: this.toasts.createFailDetail,
    });
  }

  showUpdateSuccessToast(toast: ToastService) {
    toast.add({
      severity: 'info',
      summary: this.toasts.updateSuccessSummary,
      detail: this.toasts.updateSuccessDetail,
    });
  }

  showUpdateFailToast(toast: ToastService) {
    toast.add({
      severity: 'error',
      summary: this.toasts.updateFailSummary,
      detail: this.toasts.updateFailDetail,
    });
  }

  showDeleteSuccessToast(toast: ToastService) {
    toast.add({
      severity: 'info',
      summary: this.toasts.deleteSuccessSummary,
      detail: this.toasts.deleteSuccessDetail,
    });
  }

  showDeleteFailToast(toast: ToastService) {
    toast.add({
      severity: 'error',
      summary: this.toasts.deleteFailSummary,
      detail: this.toasts.deleteFailDetail,
    });
  }

  showGenericSuccessToast(toast: ToastService) {
    this;
    toast.add({
      severity: 'info',
      summary: genericToasts.genericSuccessSummary,
      detail: genericToasts.genericSuccessDetail,
    });
  }

  showGenericFailToast(toast: ToastService) {
    this;
    toast.add({
      severity: 'error',
      summary: genericToasts.genericFailSummary,
      detail: genericToasts.genericFailDetail,
    });
  }

  async openConfirmationDialog(confirmationService: ConfirmationService, label: string) {
    return confirmationService.confirm({
      header: this.translateService.instant(this.confirmDelete.header),
      message: this.translateService.instant(this.confirmDelete.message, { label }),
      confirmLabel: this.translateService.instant(this.confirmDelete.confirmLabel),
    });
  }

  showErrorResponseToast(
    toast: ToastService,
    errorResponse?: ErrorResponse,
    showGenericError?: (toast: ToastService) => unknown,
  ) {
    const wellKnownErrors = errorResponse?.errors ?? [];
    wellKnownErrors.forEach((error) => {
      const detail =
        error.error !== WKECodes.Unknown
          ? translateWellKnownError(error)
          : error.error === WKECodes.Unknown && error.message
            ? error.message
            : genericToasts.genericFailDetail;
      toast.add({
        severity: 'error',
        summary: this.translateService.instant(genericToasts.genericFailSummary),
        detail: this.translateService.instant(detail),
      });
    });

    if (wellKnownErrors.length) {
      return;
    }

    if (showGenericError) {
      showGenericError(toast);
    } else {
      // think about displaying a message about status codes
      this.showGenericFailToast(toast);
    }
  }
}
